//////////////////
// IMPORTS
//////////////////

// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { createFragmentContainer } from "react-relay";
import classnames from "classnames";

// event-summary-color-category imports
import { eventSummaryColorCategoryFragments } from "./EventSummaryColorCategory.gql";

// state
import { IReduxState } from "@reducers/root.reducer";
import { addUserTags } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { setCurrentTip } from "@actions/week-view.actions";
import { WeekViewCurrentTip } from "@actions/week-view.actions";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { toggleExpanded } from "@actions/settings.actions";

// tracking
import { track, TrackingEvents } from "@util/analytics.util";

// other components
import { EventCategoryPicker } from "@clockwise/web-commons/src/ui/event-category-picker";

// util
import { logger } from "@util/logger.util";
import { getEventCategory } from "@clockwise/client-commons/src/util/event-category-coloring";
import { createTagForMutation, Tags } from "@clockwise/client-commons/src/util/event-tag";
import { createEventParentRelayIdFromEvent } from "@util/event.util";
import { getNextWeekViewTip, updateWeekViewFlags } from "@util/flags.util";
import { getCurrentEnvironment } from "@src/state/relay-environment";

// material-ui
import { makeStyles } from "@material-ui/styles";

// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import { getValue } from "@src/util/errorable.util";
import TutorialMeetingColorCategory from "@components/tutorial-tip/tutorial-meeting-color-categories/TutorialMeetingColorCategory";

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    padding: "0px 0px 16px",
  },
  pickerContainer: {
    display: "flex",
    alignItems: "flex-start",
    fontWeight: 400,
    padding: 0,
    width: "fit-content",
  },
  labelIcon: {
    color: greys.blueGrey,
    width: 18,
    height: 18,
    margin: "5px 12px 0px 0px",
    flexShrink: 0,
  },
});

type Props = {
  user: ISchema.IUser;
  org: ISchema.IOrg;
  event: ISchema.IEvent;
};

//////////////////
// COMPONENT
//////////////////
const EventSummaryColorCategoryCmpt = ({ org, event, user }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentTip = useSelector((state: IReduxState) => state.weekView.currentTip);
  const environment = getCurrentEnvironment();

  const eventCategory = getEventCategory(event, user.id);
  const eventColoringSettings = getValue(org.eventColoringSettingsErrorable);

  if (!eventColoringSettings) {
    return null;
  }

  React.useEffect(() => {
    const flagsErrorable = user.webAppFlagsErrorable;

    if (currentTip === WeekViewCurrentTip.EventDetails) {
      updateWeekViewFlags(environment, currentTip).finally(() =>
        dispatch(setCurrentTip(getNextWeekViewTip(flagsErrorable, currentTip))),
      );
    }
  }, []);

  const onError = (error: string) => {
    logger.error(`Failed to save user category selection`, error);
    dispatch(snackBarOpen("Sorry, failed to save. Please try again."));
  };

  const onSuccess = (message: string) => {
    dispatch(snackBarOpen(message));
  };

  const saveTag = (
    mutationFn: typeof addUserTags,
    tag: ISchema.ITagInput,
    successMessage: string,
  ) => {
    const eventParentRelayId = createEventParentRelayIdFromEvent(event);

    mutationFn(
      environment,
      org.id,
      eventParentRelayId,
      [tag],
      () => onSuccess(successMessage),
      (e: string) => onError(e),
    );
  };

  const onSelectCategory = (eventCategory: string) => {
    const tag = createTagForMutation(Tags.EventColoringCategory, eventCategory);
    const successMessage = "Saved new category selection.";
    saveTag(addUserTags, tag, successMessage);
    track(TrackingEvents.COLOR_CATEGORIES.DROPDOWN_CHANGE, { eventCategory, source: "sidebar" });
  };

  const openColorCodingSettings = () => {
    track(TrackingEvents.COLOR_CATEGORIES.DROPDOWN_SETTINGS_CLICKED);
    dispatch(setSettingsDialogOpen(true));
    dispatch(toggleExpanded("expandColorCoding"));
  };

  return (
    <div className={classnames(classes.container)}>
      <TutorialMeetingColorCategory userWebAppFlags={user.webAppFlagsErrorable} />
      {eventColoringSettings && (
        <div className={classes.pickerContainer}>
          <EventCategoryPicker
            event={event}
            eventColoringSettings={eventColoringSettings}
            selectedCategory={eventCategory}
            onSelectCategory={(category: string) => onSelectCategory(category)}
            isTransparentAndNotAllDayEvent={
              !event.isAllDay && event.transparency === ISchema.TransparencyEnum.Transparent
            }
            openColorCodingSettings={openColorCodingSettings}
          />
        </div>
      )}
    </div>
  );
};

//////////////////
// RELAY
//////////////////
export const EventSummaryColorCategory = createFragmentContainer<Props>(
  EventSummaryColorCategoryCmpt,
  eventSummaryColorCategoryFragments,
);
